import { Filter, type SelectItem } from '@vrplatform/components';
import {
  type gqlV2,
  useInfiniteQuery,
  useQuery,
  useTeamRole,
  useTeam,
} from '@vrplatform/data';
import { CopyIcon, Icon, type IconDefinition } from '@vrplatform/icons';
import { InfiniteTable, type MRT_ColumnDef } from '@vrplatform/table';
import {
  AutomationIcon,
  EllipsisMenuItem,
} from '@vrplatform/components-legacy';
import { showSuccessNotification } from '@vrplatform/theme';
import { type Maybe, day, emptyUUID, sortBy } from '@vrplatform/utils';
import { Avatar, Group, Text, useMantineColorScheme } from '@mantine/core';
import { useSetState } from '@mantine/hooks';
import {
  getActionMessage,
  whereAutomationTemplateVisibility,
  whereSyncs,
} from '@vrplatform/ui-common';
import { useCallback, useMemo } from 'react';
import { HiddenFeatureIndicator } from '../../../components';
import { useAutomationHistoryDrawer } from '../../../hooks';
import { AutomationNameCell } from '../../../views/automations/views/overview-automations/_components/AutomationNameCell';
import { getSync } from '../../automation-history-drawer/useAutomationHistoryDrawerQuery';

export const STORYBOOK_CONNECTION_SYNCS_QUERY_KEY = 'connection-syncs-table';

type FilterState = {
  search: string;
  type: Maybe<string>; // automationId OR "extract"
};

type Props = { connectionId: string; tenantId: string };

export const ConnectionSyncsTable = ({ connectionId, tenantId }: Props) => {
  const { open: openAutomationHistoryDrawer } = useAutomationHistoryDrawer();
  const initial: FilterState = { search: '', type: null };
  const [filter, setFilter] = useSetState<FilterState>(initial);
  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const resetFilter = useCallback(() => () => setFilter(initial), []);
  const { colorScheme } = useMantineColorScheme();

  const { isVrpAdmin } = useTeamRole();
  const [{ id: teamId }] = useTeam();

  const queryData = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      if (!args.connectionId)
        return {
          list: [],
          aggregate: 0,
        };

      const where = whereSyncs({
        tenantId: args.teamId,
        search: args.search,
        connectionId: args.connectionId,
        type: args.type,
      });

      const aggregate = q.syncAggregate({ where }).aggregate?.count() || 0;

      const messageOverwrites =
        offset !== 0
          ? []
          : q
              .issueMessageOverwrites({
                order_by: [{ pattern: 'asc_nulls_last' }],
              })
              .map((o) => ({
                pattern: o.pattern || '',
                message: o.message || '',
              }));

      const list = q
        .syncs({
          where,
          limit,
          offset,
          order_by: [{ createdAt: 'desc_nulls_last' }],
        })
        .map(getSync);

      return {
        list,
        aggregate,
        messageOverwrites,
      };
    },
    {
      queryKey: 'connections',
      variables: {
        connectionId: connectionId || emptyUUID,
        search: filter.search?.trim(),
        type: filter.type,
        teamId,
      },
      skip: !connectionId,
      storybookQueryKey: STORYBOOK_CONNECTION_SYNCS_QUERY_KEY,
    }
  );

  const messageOverwrites = useMemo(
    () => queryData.data?.pages?.[0]?.messageOverwrites || [],
    [queryData.data?.pages?.[0]?.messageOverwrites]
  );

  const columns = useMemo<MRT_ColumnDef<ConnectionExtractRow>[]>(
    () => [
      {
        accessorKey: 'title',
        enableSorting: true,
        header: 'Recent fetches',
        // maxSize: 120,
        Cell: ({ row }) => {
          const data = row.original;

          let color: 'green' | 'yellow' | 'red' = 'green';
          let icon: IconDefinition = 'CheckIcon';

          if (data.status === 'failed') {
            color = 'red';
            icon = 'CrossIcon';
          }

          if (data.status === 'queued' || data.status === 'started') {
            color = 'yellow';
            icon = 'RefreshCwIcon';
          }

          if (!data.status) return null;

          return (
            <Group wrap="nowrap">
              <Avatar size="sm" radius="xl" color={color}>
                <Icon
                  icon={icon}
                  color={(theme) => theme.colors[color][7]}
                  size={14}
                />
              </Avatar>
              <Text>
                {getActionMessage(data.title || '-', messageOverwrites)}
              </Text>
            </Group>
          );
        },
      },

      {
        accessorKey: 'type',
        header: 'Date',
        Cell: ({ row }) => {
          const data = row.original;
          if (data.isExtractWorkflow)
            return (
              <Group wrap="nowrap" gap={'xs'}>
                <Avatar
                  src={data.extractConnection.icon}
                  size={18}
                  radius="lg"
                />
                <Text>
                  {data.extractConnection.appName
                    ? `${data.extractConnection.appName} `
                    : ''}
                  Data Fetching
                </Text>
              </Group>
            );

          return (
            <AutomationNameCell
              name={data?.automation.name}
              templateType={data.automation.templateType}
              templateVisibility={data.automation.visibility}
            />
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Date',
        mantineTableBodyCellProps: { align: 'right' },
        mantineTableHeadCellProps: { align: 'right' },
        maxSize: 150,
        Cell: ({ row }) => {
          return (
            <Text sx={{ flexShrink: 0 }}>
              {day(row.original.createdAt).format('MMM DD, YYYY HH:mm')}
            </Text>
          );
        },
      },
    ],
    [messageOverwrites]
  );

  type ConnectionExtractRow = NonNullable<
    (typeof queryData)['data']
  >['pages'][number]['list'][number];

  return (
    <InfiniteTable
      columns={columns}
      table={{
        hideHeader: true,
        hideSettings: true,
        onRowClick: {
          handler: (row) => {
            openAutomationHistoryDrawer(row.original.id, { type: 'push' });
          },
        },
      }}
      queryData={queryData}
      styles={{
        row: ({ row }, theme) => {
          const isHiddenAutomation =
            row.original.automation?.visibility === 'admin' ||
            row.original.automation?.visibility === 'partnerAdmin';

          if (!isHiddenAutomation) return {};

          return {
            backgroundColor:
              colorScheme === 'dark'
                ? theme.colors.dark[9]
                : theme.colors.gray[1],
          };
        },
      }}
      rowMenu={{
        hideMenu: () => isVrpAdmin === false,
        menuItems: ({ row }) => {
          const syncId = row.original.id;
          const message = row.original.title;
          return (
            <HiddenFeatureIndicator permission="vrp-admin">
              <EllipsisMenuItem
                customIcon={<CopyIcon size={18} />}
                disabled={!message}
                onClick={() =>
                  navigator.clipboard.writeText(message || '').then(() =>
                    showSuccessNotification({
                      message: 'Message copied to clipboard',
                      icon: null,
                      autoClose: 1000,
                    })
                  )
                }
              >
                Copy message
              </EllipsisMenuItem>

              <EllipsisMenuItem
                customIcon={<CopyIcon size={18} />}
                disabled={!syncId}
                onClick={() =>
                  navigator.clipboard.writeText(syncId!).then(() =>
                    showSuccessNotification({
                      message: 'Workflow ID copied to clipboard',
                      icon: null,
                      autoClose: 1000,
                    })
                  )
                }
              >
                Copy sync ID
              </EllipsisMenuItem>
            </HiddenFeatureIndicator>
          );
        },
      }}
      resetFilter={resetFilter}
    >
      <Group gap="xs">
        <Filter.Search
          value={filter.search}
          setValue={(s) => setFilter({ search: s })}
        />
        <TypeFilter
          connectionId={connectionId}
          tenantId={tenantId}
          type={filter.type}
          setType={(v) => setFilter({ type: v })}
        />
      </Group>
    </InfiniteTable>
  );
};

const TypeFilter = ({
  connectionId,
  setType,
  tenantId,
  type,
}: Props & { type: Maybe<string>; setType: (v: Maybe<string>) => void }) => {
  const { isPartnerAdmin, isSuperAdmin, isVrpAdmin } = useTeamRole();
  const queryData = useQuery(
    (q, { connectionId, isPartnerAdmin, isSuperAdmin, isVrpAdmin }) => {
      if (!connectionId || !tenantId)
        return {
          list: [],
        };

      const where: gqlV2.sync_bool_exp = {
        automation: {
          ttemplate: {
            visibility: whereAutomationTemplateVisibility({
              isPartnerAdmin,
              isSuperAdmin,
              isVrpAdmin,
            }),
          },
          _or: [
            {
              leftConnectionId: { _eq: connectionId },
            },
            {
              rightConnectionId: { _eq: connectionId },
            },
          ],
        },
      };

      const connection = q.connectionById({ id: connectionId });

      const extracts: SelectItem[] = [
        {
          label: `${connection.app?.name} Data Fetching`,
          value: 'extract',
          icon: <Avatar size={14} src={connection.app?.iconRound} />,
          pinned: true,
        },
      ];

      const plans = sortBy(
        q
          .syncs({
            where,
            order_by: [{ automationId: 'desc_nulls_last' }],
            distinct_on: ['automationId'],
          })
          .map<SelectItem>((jobPlan) => {
            const automation = jobPlan?.automation;

            return {
              value: jobPlan?.automationId!,
              label:
                automation?.title || automation?.ttemplate?.title || 'No name',
              icon: (
                <AutomationIcon
                  templateType={automation?.ttemplate?.type}
                  size={16}
                />
              ),
            };
          }),
        'label'
      );

      return {
        list: [...extracts, ...plans],
      };
    },
    {
      variables: {
        connectionId: connectionId || emptyUUID,
        tenantId,
        isSuperAdmin,
        isVrpAdmin,
        isPartnerAdmin,
      },
    }
  );

  const value = type
    ? queryData.data?.list.find((x) => x.value === type) || null
    : null;

  return (
    <Filter.Select
      label="Type"
      type="single"
      value={value}
      setValue={(newValue) => {
        setType(newValue?.value || null);
      }}
      data={{
        options: queryData.data?.list || [],
        loading: queryData.isLoading,
      }}
    />
  );
};
